import React, { useEffect, useState, useRef } from 'react';
import { useForm, Controller, type Control } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getThread, inviteToThread, unsubscribeFromThread, type InviteToThreadRequest, type Thread } from '../../../services/MessageService';
import { useNotification } from '../../../components/Notifications';
import { getUsers, newUser, type UserDTO } from '../../../services/AuthService';

export function ManageParticipants() {
  const { register, handleSubmit, formState: { errors }, control, reset, setValue } = useForm({
    defaultValues: {
      selectedUsers: [],
      emails: '',
      role: 'User'
    }
  });
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState(null);
  const [ searchQuery, setSearchQuery ] = useState('');
  const dropdownRef = useRef(null);
  const { id } = useParams();
  const { addNotification } = useNotification();
  const [ thread, setThread ] = useState<Thread>({
    id: 0,
    createdId: 0,
    createdTs: null,
    recipients: [],
    subject: '',
    ts: null,
    messages: [],
    messagesCount: 0
  });
  const [ users, setUsers ] = useState<UserDTO[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadThread();
  }, []);

  useEffect(() => {
    setUsers(thread.recipients)
    setValue('selectedUsers', thread.recipients.map(u => u.id))
  }, [thread]);

  const loadThread = () => {
    getThread(Number(id))
      .then(data => {
        console.log('Thread data:', data);
        setThread(data);
      })
      .catch(err => {
        addNotification(`Error loading message ${id} ${err.message}`, 'error');
      });
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user =>
    `${user.firstName} ${user.lastName} ${user.email}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const unselected = thread.recipients
        .map(u => u.id)
        .filter(id => !data.selectedUsers.includes(id));

      const req: InviteToThreadRequest = {
        threadId: thread.id,
        users: unselected
      };
      const t = await unsubscribeFromThread(req)
      setLoading(false);
      reset();
      addNotification('Users have been successfully updated', 'success')
      navigate(-1)

    } catch (err) {
      setLoading(false);
      setError(`Error updating subscriptions: ${err.message}`);
    }
  };

  const roleOptions = ['Guest', 'User', 'Admin'];

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="flex items-center justify-center">
      <div className="w-full space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">Subscribed Participants</h2>
          </div>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search emails..."
              className="pr-10 pl-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm text-gray-800 placeholder-gray-400 w-full"
              aria-label="Search users by name or email"
            />
            <i
              className="fa-solid fa-magnifying-glass text-indigo-500 absolute right-3 top-1/2 transform -translate-y-1/2 text-lg"
            ></i>
          </div>
        </div>
        {error && (
          <p className="text-red-600 text-sm text-center">{error}</p>
        )}
        {loading && (
          <div className="text-center">
            <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4 mx-auto"></div>
            <p className="text-gray-600">Updating participants...</p>
          </div>
        )}
        {!loading && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-1">
              <Controller
                name="selectedUsers"
                control={control}
                render={({ field }) => (
                  <div className="border border-gray-300 rounded-md bg-white max-h-60 overflow-y-auto">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <label
                          key={user.id}
                          className="flex items-center px-3 py-2 hover:bg-indigo-100 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            value={user.id}
                            checked={field.value.includes(user.id)}
                            onChange={(e) => {
                              const newValue = e.target.checked
                                ? [...field.value, user.id]
                                : field.value.filter((id) => id !== user.id);
                              field.onChange(newValue);
                            }}
                            className="h-4 w-4 text-indigo-500 border-gray-300 rounded cursor-pointer focus:ring-indigo-600"
                            aria-label={`Select ${user.firstName} ${user.lastName}`}
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {user.firstName} {user.lastName} ({user.email})
                          </span>
                        </label>
                      ))
                    ) : (
                      <p className="px-3 py-2 text-sm text-gray-500">No users found</p>
                    )}
                  </div>
                )}
              />
              {errors.selectedUsers && (
                <p className="text-red-600 text-sm">{errors.selectedUsers.message}</p>
              )}
            </div>
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
              >
                Update Participants
              </button>
              <div
                onClick={() => navigate(-1)}
                className="cursor-pointer flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-800 bg-gray-200 hover:bg-gray-300 text-center"
              >
                Cancel
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};